import type { FieldWrapperInjectedProps } from "@birdwatch/ui/form";
import { cn } from "cn";
import { useMemo } from "react";
import Avatar from "@/components/ui/avatar/avatar";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { BwUserLine } from "@/components/ui/icons";
import { useAllVendors } from "@/features/office/vendors/list/_api/use-all-vendors";

export type VendorSelectProps = FieldWrapperInjectedProps & {
  value: string | null;
  onChange: (value: string | null) => void;
  showAvatar?: boolean;
  /** An X in the trigger clears the vendor. */
  clearable?: boolean;
  disabled?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  "data-test"?: string;
};

export const VendorSelect = ({
  value,
  onChange,
  showAvatar = true,
  clearable,
  disabled,
  defaultOpen = false,
  onOpenChange,
  className,
  ...props
}: VendorSelectProps) => {
  const { data: vendors = [] } = useAllVendors();

  const options = useMemo<ComboboxOption[]>(
    () =>
      vendors.map((vendor) => ({
        value: vendor.id,
        label: vendor.name,
        keywords: [vendor.name],
        detail: (
          <div className="flex grow items-center gap-2">
            {showAvatar && (
              <Avatar
                src={vendor.avatarUrl || ""}
                name={vendor.name}
                size="sm"
              />
            )}
            <span className="grow line-clamp-1 break-all">{vendor.name}</span>
          </div>
        ),
      })),
    [vendors, showAvatar],
  );

  return (
    <Combobox
      data-test="line-item-vendor-select-trigger"
      {...props}
      value={value}
      onChange={onChange}
      clearable={clearable}
      clearLabel="Clear vendor"
      options={options}
      disabled={disabled}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      className={className}
      searchPlaceholder="Search vendor..."
      emptyText="No vendor found."
      placeholder="Vendor"
      renderTrigger={(selected) =>
        selected ? (
          selected.detail
        ) : (
          <div className="flex grow items-center gap-2">
            {showAvatar && (
              <div
                className={cn(
                  /* sorted */
                  "flex overflow-hidden",
                  "shrink-0 items-center justify-center",
                  "h-5 w-5",
                  "rounded-full border border-dashed border-border-dark-low",
                )}
              >
                <BwUserLine className="text-text-dark-min size-4" />
              </div>
            )}
            <p className="line-clamp-1 break-all text-text-dark-min grow">
              Vendor
            </p>
          </div>
        )
      }
    />
  );
};
